/* eslint-disable operator-linebreak */
import React from 'react';
import { FormApi } from 'final-form';
import { useField } from 'react-final-form';
import { FormControlLabel, FormControlLabelClassKey } from '@material-ui/core';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import TableDatePicker from 'ui/TableInputs/TableDatePicker/TableDatePicker';
import Input, { Select, SelectItem, AmountInput } from 'ui/Input';
import Switch from 'ui/Switch/Switch';
import { IAccount } from 'store/accounts.model';
import { MAX_ACCOUNT_LENGTH, TransferFrequencyList } from 'utils/constants';
import { onlyNCharacters, onlyNumbers } from 'utils/formatters';
import { IFormValues, ITransferActions, ITransfersState } from './BulkManualEntry.model';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './BulkManualEntry.module.css';

type FormType = FormApi<IFormValues, Partial<IFormValues>>;

const renderSourceAccountField = (accounts: IAccount[], submitting: boolean) => (
  <Select name="sourceAccount" label="Source Account" disabled={submitting}>
    {accounts.map(({ accountNumber }) => (
      <SelectItem value={accountNumber} key={accountNumber}>
        {accountNumber}
      </SelectItem>
    ))}
  </Select>
);

const renderAmountField = (
  submitting: boolean,
  name: string,
  values: IFormValues,
  actions: ITransferActions,
  index: number,
) => {
  const handleCharge = () => {
    const { getTransactionCharge } = actions;
    const currentBeneficiary = values?.transferData?.[index];
    if (values) {
      if (currentBeneficiary?.transactionAmount && currentBeneficiary?.transactionType) {
        getTransactionCharge(index);
      }
    }
  };

  return (
    <AmountInput
      name={`${name}.transactionAmount`}
      label="Amount"
      disabled={submitting || !values.sourceAccount}
      onBlur={handleCharge}
    />
  );
};

export const renderAccountNumberManualField = (
  values: IFormValues,
  name: string,
  actions: ITransferActions,
  index: number,
) => {
  const { findBeneficiaryName } = actions;
  const handleBlur = () => {
    const currentBeneficiary = values?.transferData?.[index];
    if (values) {
      if (currentBeneficiary?.beneficiaryAccount) {
        findBeneficiaryName(index);
      }
    }
  };

  const characterLimits = MAX_ACCOUNT_LENGTH;

  return (
    <Input
      name={`${name}.beneficiaryAccount`}
      label="Account Number"
      disabled={!values.sourceAccount}
      fieldProps={{
        parse: (value: string) => onlyNCharacters(characterLimits)(onlyNumbers(value)),
      }}
      onBlur={handleBlur}
    />
  );
};

// Export Fields

export const renderScheduleControl = (
  toggleScheduleTransfer: () => void,
  toggleSchedule: boolean | null,
  classes: Partial<ClassNameMap<FormControlLabelClassKey>> | undefined,
) => (
  <FormControlLabel
    control={<Switch />}
    checked={toggleSchedule === null ? false : toggleSchedule}
    onChange={toggleScheduleTransfer}
    label="Schedule transfer for later"
    labelPlacement="end"
    classes={classes}
    name="scheduleTransfer"
  />
);

export const renderScheduleForm = (
  submitting: boolean,
  dateFilterChange: (name: string, value?: string | null | undefined) => void,
  handleClearDateInput: (name: string, value?: string | null | undefined) => void,
  values: IFormValues,
) => (
  <>
    <Select name="frequency" label="Frequency" disabled={submitting}>
      {TransferFrequencyList.map(({ label, value }) => (
        <SelectItem value={value} key={value}>
          {label}
        </SelectItem>
      ))}
    </Select>
    <TableDatePicker
      name="specialDate"
      label="Special Date"
      onChange={dateFilterChange}
      onClickClear={handleClearDateInput}
      disabled={submitting || values.frequency !== 'specialDate'}
    />
  </>
);

export const renderTransferFieldsFrom = (
  values: IFormValues,
  accounts: IAccount[],
  state: ITransfersState,
  actions: ITransferActions,
  form: FormApi<IFormValues, Partial<IFormValues>>,
  submitting: boolean,
  toggleSchedule: boolean | null,
  toggleSaveBeneficiary: () => void,
  classes: Partial<ClassNameMap<FormControlLabelClassKey>> | ClassNameMap<'label' | 'root'> | undefined,
  dateFilterChange: (name: string, value?: string | null | undefined) => void,
  handleClearDateInput: (name: string, value?: string | null | undefined) => void,
) => (
  <>
    {renderSourceAccountField(accounts, submitting)}
    {renderScheduleControl(toggleSaveBeneficiary, toggleSchedule, classes)}
    {values.scheduleTransfer && renderScheduleForm(submitting, dateFilterChange, handleClearDateInput, values)}
  </>
);

const renderNonInstantBeneficiaryNameField = (name: string) => (
  <Input name={`${name}.beneficiaryName`} label="Beneficiary Name" />
);

const renderCharge = (name: string) => (
  <Input name={`${name}.charge`} label="Name" disabled className={styles.hidden} />
);

const renderbeneficiaryId = (name: string) => (
  <Input name={`${name}.beneficiaryId`} label="Name" disabled className={styles.hidden} />
);

const renderDescriptionField = (name: string, values: IFormValues) => (
  <Input
    name={`${name}.transactionDescription`}
    label="Description"
    disabled={!values.sourceAccount}
    fieldProps={{
      parse: onlyNCharacters(25),
    }}
  />
);

const onSelectBank =
(form: FormType, actions: ITransferActions, index: number) =>
  ({ target }) => {
    const { handleSelectBank } = actions;
    const { value: selectedbank, name: bankFieldName } = target as HTMLInputElement;
    form.mutators.changeFieldValue(bankFieldName, selectedbank);
    handleSelectBank(index);
  };

/** Changes options and clears field B when field A changes */
const PickOptions = ({ a, b, optionsMap = [], children }) => {
  const aField = useField(a, { subscription: { value: true } });
  const bField = useField(b, { subscription: {} });
  const aValue = aField.input.value;
  const changeB = bField.input.onChange;
  const [list, setList] = React.useState(optionsMap[aValue]);

  React.useEffect(() => {
    setList(optionsMap[aValue]);

    return function cleanup() {
      // changeB(undefined); // clear B
    };
  }, [aValue, changeB, optionsMap]);
  return children(list || []);
};

const renderBankField = (
  banks: ITransfersState['banks'],
  values: IFormValues,
  isLookingUp: ITransfersState['isLookingUp'],
  actions: ITransferActions,
  form: FormType,
  names: string,
  index: number,
  banksList: any,
) => (
  <>
    <PickOptions
      a={`${names}.transactionType`}
      b={`${names}.beneficiaryBank`}
      optionsMap={banksList}
    >
      {(list: any) => (
        <Select
          name={`${names}.beneficiaryBank`}
          label="Bank"
          disabled={isLookingUp || !values.sourceAccount}
          SelectProps={
              form
                ? {
                  onChange: onSelectBank(form, actions, index),
                }
                : undefined
            }
          className={styles.selectWidth}
        >
          {list?.map(({ name, code }) => (
            <SelectItem value={code} key={code}>
              {name}
            </SelectItem>
          ))}
        </Select>
      )}
    </PickOptions>
  </>
);


// const onSelectBankBranch =
// (form: FormType) =>
//   ({ target }) => {
//     const { value: selectedbankBranch, name: bankBranchFieldName } = target as HTMLInputElement;
//     form.mutators.changeFieldValue(bankBranchFieldName, selectedbankBranch);
//   };
const renderBankBranchField = (
  bankBranch: ITransfersState['bankBranch'],
  isLookingUp: ITransfersState['isLookingUp'],
  actions: ITransferActions,
  form: FormType,
  names: string,
  banksBranchList: any,
) => (
  <>
    <PickOptions a={`${names}.beneficiaryBank`} b={`${names}.bankBranch`} optionsMap={banksBranchList}>
      {(list: any) => (
        <Select
          name={`${names}.bankBranch`}
          label="Bank Branch"
          disabled={isLookingUp}
          className={styles.selectWidth}
          // SelectProps={
          //     form
          //       ? {
          //         onChange: onSelectBankBranch(form),
          //       }
          //       : undefined
          //   }
        >
          {list?.map(({ BranchName, BranchCode, BranchSortCode }) => (
            <SelectItem value={BranchCode} key={BranchSortCode}>
              {BranchName}
            </SelectItem>
          ))}
        </Select>
      )}
    </PickOptions>
  </>
);

export const renderTransferTypeField = (
  names: string,
  index: number,
  values: IFormValues,
  types: ITransfersState['types'],
  isLookingUp: ITransfersState['isLookingUp'],
  form: FormType,
  handleTypeChange: () => void,
) => {
  const handleChange = ({ target }) => {
    form.mutators.changeFieldValue(target?.name, target?.value); // Override default handleChange()
    form.mutators.changeFieldValue('destinationBankCode', undefined);
    handleTypeChange();
  };
  const beneficiaryData = values?.transferData;
  const currentBen = beneficiaryData[index];
  return (
    <Select
      name={`${names}.transactionType`}
      label="Transfer Type"
      disabled={isLookingUp || !values.sourceAccount || currentBen?.transactionType?.length > 1}
      SelectProps={{
        onChange: handleChange,
      }}
      className={styles.selectWidth}
    >
      {types.map(({ name, key }) => (
        <SelectItem value={key} key={key}>
          {name}
        </SelectItem>
      ))}
    </Select>
  );
};

export const renderTransferToFields = (
  name: string,
  values: IFormValues,
  accounts: IAccount[],
  state: ITransfersState,
  actions: ITransferActions,
  form: FormApi<IFormValues, Partial<IFormValues>>,
  submitting: boolean,
  banksBranchList,
  index: number,
  handleTypeChange: () => void,
  banksList,
) => (
  <>
    {renderTransferTypeField(name, index, values, state.types, state.isLookingUp, form, handleTypeChange)}
    {renderAccountNumberManualField(values, name, actions, index)}
    {renderBankField(state.banks, values, state.isLookingUp, actions, form, name, index, banksList)}
    {renderBankBranchField(state.bankBranch, state.isLookingUp, actions, form, name, banksBranchList)}
    {renderNonInstantBeneficiaryNameField(name)}
    {renderAmountField(submitting, name, values, actions, index)}
    {renderDescriptionField(name, values)}
    {renderCharge(name)}
    {renderbeneficiaryId(name)}
  </>
);
